import { useParams, useLocation, useNavigationType } from 'react-router-dom'
import { GenericWizard, WizardStep, useWizard } from '@/components/common/wizard/GenericWizard'
import { useQuery, gql } from '@apollo/client'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { Modal, Button } from 'react-bootstrap'
import Megaphone from '@/components/common/icons/Megaphone'
import { useContext, useEffect, useState, useRef } from 'react'
import { SettingsContext } from '@/context/settings-context'
import { CubeLoader } from '@/components/ui/cube-loader'
import { getAllStages } from '@/utils/ad-campaigns-helpers'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { CORE_CAMPAIGN_FIELDS } from '@/graphql/campaign-fragments'
import { Switch } from '@/components/ui/switch'

// GraphQL query to get campaign by ID
const GET_CAMPAIGN_BY_ID = gql`
    ${CORE_CAMPAIGN_FIELDS}
    query GetCampaignById($campaignId: ID!) {
        campaigns(where: { id: $campaignId }) {
            ...CoreCampaignFields
        }
    }
`

// Import all screen components
import CampaignObjective from './screens/CampaignObjective'
import LocationTargeting from './screens/LocationTargeting'
import SalesFunnelSetup from './screens/SalesFunnelSetup/Screen'
import AttachAudience from './screens/SalesFunnelSetup/AttachAudience'
import AudienceTargeting from './screens/AudienceTargeting/Screen'
import AdsSelection from './screens/AdsSelection'
import BudgetSchedule from './screens/BudgetSchedule/Screen'
import ReviewLaunch from './screens/ReviewLaunch'
import EmptyWizard from './EmptyWizard'

// Screen mappings for ad campaign wizard
const adCampaignScreensMap = {
    campaignObjective: CampaignObjective,
    locationTargeting: LocationTargeting,
    salesFunnelSetup: SalesFunnelSetup,
    attachAudience: AttachAudience,
    audienceTargeting: AudienceTargeting,
    adsSelection: AdsSelection,
    budgetSchedule: BudgetSchedule,
    reviewLaunch: ReviewLaunch,
}

const PopupsSwitch = () => {
    const { showGuidePopups, setShowGuidePopups } = useWizard()

    return (
        <div className="tw:flex tw:items-center tw:gap-2">
            <Switch checked={showGuidePopups} onCheckedChange={setShowGuidePopups} />
            <span className="tw:text-sm tw:font-semibold">Help popups</span>
        </div>
    )
}

// Unfinished Campaign Modal Component
const UnfinishedCampaignModal = ({ show, onClose, onContinue, onStartOver }) => {
    const { theme } = useContext(SettingsContext)
    const isLight = theme === 'light'

    return (
        <Modal show={show} onHide={onClose} centered backdrop="static" contentClassName="border rounded">
            <Modal.Header closeButton className="border-bottom p-3">
                <Modal.Title>Unfinished Campaign</Modal.Title>
            </Modal.Header>

            <Modal.Body className="border-bottom p-0 pt-2">
                <div
                    className={`w-100 d-flex flex-column align-items-center px-4 py-5 ${
                        isLight ? 'text-dark bg-white' : 'text-white'
                    }`}
                >
                    <div
                        className="mx-auto d-flex align-items-center justify-content-center rounded-circle mb-4"
                        style={{
                            width: '80px',
                            height: '80px',
                            backgroundColor: 'rgba(75, 85, 99, 0.8)', // Gray background for the icon
                        }}
                    >
                        <Megaphone />
                    </div>

                    <div className="mb-2 pb-0 text-center" style={{ maxWidth: 355, width: '100%', fontSize: '14px' }}>
                        It looks like you have an unfinished campaign. Would you like to continue editing it or start a
                        new one from scratch?
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-center gap-3 px-4 pb-4">
                <Button
                    variant="outline-primary"
                    size="sm"
                    className="media-buying-button-action"
                    style={{
                        minWidth: '160px',
                        fontSize: '14px',
                    }}
                    onClick={onStartOver}
                >
                    Start New
                </Button>

                <Button
                    variant="primary"
                    size="sm"
                    style={{
                        minWidth: '160px',
                        fontSize: '14px',
                    }}
                    onClick={onContinue}
                >
                    Continue Draft
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

// Navigation Detection Component
const NavigationDetector = ({ campaignId, isEditFlow, children }) => {
    const location = useLocation()
    const navigationType = useNavigationType()
    const [showDialog, setShowDialog] = useState(false)
    const hasCheckedNavigation = useRef(false)
    const [wizardReset, setWizardReset] = useState(false)

    useEffect(() => {
        // Only check once when component mounts
        if (hasCheckedNavigation.current) return
        hasCheckedNavigation.current = true

        // Never show the unfinished campaign dialog in edit mode
        if (isEditFlow) return

        const storageKey = `adCampaignWizardState-${campaignId}`
        const savedState = localStorage.getItem(storageKey)

        if (savedState) {
            try {
                const state = JSON.parse(savedState)

                // Check if wizard is not on first step (campaignObjective)
                const isNotFirstStep = state.currentStepName && state.currentStepName !== 'campaignObjective'

                if (isNotFirstStep) {
                    // Use React Router's navigation type to determine how user arrived
                    // PUSH = navigated from another page (show dialog)
                    // POP = refresh, back/forward, or direct URL (don't show dialog)
                    // REPLACE = location was replaced (don't show dialog)

                    if (navigationType === 'PUSH') {
                        setShowDialog(true)
                    }
                }
            } catch (error) {
                console.warn('Failed to parse wizard state:', error)
            }
        }
    }, [campaignId, isEditFlow])

    const handleContinue = () => {
        setShowDialog(false)
    }

    const handleStartOver = () => {
        // Clear localStorage manually since we're outside WizardProvider
        const storageKey = `adCampaignWizardState-${campaignId}`
        localStorage.removeItem(storageKey)
        setShowDialog(false)
        // Trigger a reset by changing a key that will remount the GenericWizard
        setWizardReset((prev) => !prev)
    }

    return (
        <>
            <UnfinishedCampaignModal
                show={showDialog}
                onClose={() => setShowDialog(false)}
                onContinue={handleContinue}
                onStartOver={handleStartOver}
            />
            <div key={wizardReset}>{children}</div>
        </>
    )
}

// Main Ad Campaign Wizard Component
export const AdCampaignWizard = ({
    isEditMode = false,
    isEditAdSetMode = false,
    isDuplicateMode = false,
    adCampaignId,
    adSetId,
    editInitialState,
    duplicateInitialState,
}) => {
    const { id } = useParams()
    const [loading, setLoading] = useState(true)
    const [initialState, setInitialState] = useState({})
    const mediaBuyingContext = useMediaBuyingContext()
    const { connectedPublishers } = mediaBuyingContext
    const publisher = connectedPublishers ? Object.values(connectedPublishers)[0] : null

    useEffect(() => {
        if (publisher?.page_id || !publisher?.pixel) mediaBuyingContext.refetch()
    }, [])

    // Fetch campaign data
    const { data: campaignData, loading: campaignLoading } = useQuery(GET_CAMPAIGN_BY_ID, {
        variables: { campaignId: id },
        skip: !id || isEditMode || isEditAdSetMode || isDuplicateMode,
    })

    // Fetch audiences data
    const { data: audiencesData, loading: audiencesLoading } = useQuery(GET_AUDIENCES)

    // Update initial state when both queries complete
    useEffect(() => {
        if (!audiencesLoading && (isEditMode || isEditAdSetMode || isDuplicateMode || !campaignLoading)) {
            if (isDuplicateMode && duplicateInitialState) {
                const allAudiences = audiencesData?.Audience?.audiences || []
                const mergedState = mergeEditStateWithDefaults(duplicateInitialState, allAudiences)
                setInitialState(mergedState)
            } else if ((isEditMode || isEditAdSetMode) && editInitialState) {
                // In edit mode, use the prefilled state from EditAdCampaign
                // but still merge default audiences so the salesFunnelSetup works
                const allAudiences = audiencesData?.Audience?.audiences || []
                const mergedState = mergeEditStateWithDefaults(editInitialState, allAudiences)
                setInitialState(mergedState)
            } else {
                const allAudiences = audiencesData?.Audience?.audiences || []
                const campaign = campaignData?.campaigns?.[0]
                const calculatedState = calculateInitialState(allAudiences, campaign, publisher)
                setInitialState(calculatedState)
            }
            setLoading(false)
        }
    }, [
        audiencesData,
        audiencesLoading,
        campaignData,
        campaignLoading,
        publisher,
        isEditMode,
        isEditAdSetMode,
        isDuplicateMode,
        editInitialState,
        duplicateInitialState,
    ])

    const mergeEditStateWithDefaults = (editState, allAudiences) => {
        // In edit mode we trust the editInitialState fully – just add publisher name if missing
        const contentCache = { ...(editState.contentCache || {}) }
        if (publisher && contentCache.prefetchedData) {
            contentCache.prefetchedData = {
                ...contentCache.prefetchedData,
                publisherNameStamp: contentCache.prefetchedData.publisherNameStamp || publisher.label || 'Turba Media',
            }
        }
        return { ...editState, contentCache }
    }

    const calculateInitialState = (allAudiences, campaign, publisher) => {
        const allStages = getAllStages()

        // Get existing cached selections (if any)
        const storageKey = `adCampaignWizardState-${id}`
        const existingState = localStorage.getItem(storageKey)
        let existingSelections = {}

        if (existingState) {
            try {
                const parsed = JSON.parse(existingState)
                existingSelections = parsed.contentCache?.salesFunnelSetup?.audienceSelections || {}
            } catch (error) {
                console.warn('Failed to parse existing wizard state:', error)
            }
        }

        const audienceSelections = {}

        allStages.map((stage) => {
            // Get all default audiences for this stage
            const defaultAudiences = allAudiences
                .filter((audience) => audience.default)
                .filter((audience) => audience.funnel_stage === stage.id)
                .map((audience) => audience.id)

            // Get existing selections for this stage
            const existingStageSelections = existingSelections[stage.id] || []

            // Merge: keep existing selections + add any new default audiences
            const mergedSelections = [...new Set([...existingStageSelections, ...defaultAudiences])]

            audienceSelections[stage.id] = mergedSelections
        })

        // Prefetch campaign name and publisher name stamp for ReviewLaunch
        const contentCache = {
            salesFunnelSetup: { audienceSelections },
        }

        if (campaign && publisher) {
            contentCache.prefetchedData = {
                campaignName: campaign.name,
                publisherNameStamp: publisher.label || 'Turba Media',
            }
        }

        return { contentCache }
    }

    if (!publisher || !publisher.page_id || !publisher.pixel) return <EmptyWizard />
    if (loading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center border tw:h-full">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    const isEditFlow = isEditMode || isEditAdSetMode
    const mode = isEditAdSetMode ? 'editAdSet' : isEditMode ? 'edit' : isDuplicateMode ? 'duplicate' : 'create'

    const wizardTitle = {
        editAdSet: 'Edit Ad Set',
        edit: 'Edit Ad Campaign',
        duplicate: 'Duplicate Ad Campaign',
        create: 'Create New Ad Campaign',
    }[mode]

    const wizardStorageKey = {
        editAdSet: `adCampaignWizardEditAdSet-${adCampaignId}-${adSetId}`,
        edit: 'adCampaignWizardEdit',
        duplicate: 'adCampaignWizardDuplicate',
        create: 'adCampaignWizardState',
    }[mode]

    const storageIdExtractor = mode === 'create' ? () => id : () => adCampaignId

    return (
        <NavigationDetector campaignId={id} isEditFlow={isEditFlow}>
            <GenericWizard
                title={wizardTitle}
                storageKey={wizardStorageKey}
                initialState={initialState}
                idExtractor={storageIdExtractor}
                screensMap={adCampaignScreensMap}
                subHeader={<PopupsSwitch />}
                startAtReviewSave={isEditFlow || undefined}
            >
                <WizardStep name="campaignObjective" label="Campaign Objective" className="tw:ml-3" visible={!isEditFlow} />
                <WizardStep name="locationTargeting" label="Location Targeting" className="tw:ml-3" visible={!isEditFlow} />
                <WizardStep name="salesFunnelSetup" label="Sales Funnel Setup" className="tw:ml-3" visible={!isEditFlow} />
                <WizardStep name="attachAudience" label="Attach Audience" visible={false} className="tw:ml-6" />
                <WizardStep name="audienceTargeting" label="Audience Targeting" className="tw:ml-3" />
                <WizardStep name="adsSelection" label="Ads Selection" className="tw:ml-3" visible={!isEditFlow} />
                <WizardStep name="budgetSchedule" label="Budget & Schedule" className="tw:ml-3" />
                <WizardStep name="reviewLaunch" label="Review & Launch" className="tw:ml-3" />
            </GenericWizard>
        </NavigationDetector>
    )
}

// Export useWizard for screen components
export { useWizard }
